import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import api from '@api/api';
import { ReactComponent as DashboardCarIcon } from '@assets/svg/dashboard/dashboard_car.svg';
import { ReactComponent as DashboardGunIcon } from '@assets/svg/dashboard/dashboard_gun.svg';
import { ReactComponent as DashboardLocationIcon } from '@assets/svg/dashboard/dashboard_location.svg';
import { ReactComponent as DashboardMemberIcon } from '@assets/svg/dashboard/dashboard_member.svg';
import { ReactComponent as DashboardPileIcon } from '@assets/svg/dashboard/dashboard_pile.svg';
import { AppDispatch } from '@store/index';
import { IRootState } from '@store/reducers';
import { setBasicInfo } from '@store/reducers/dashboardRealTimeValueSlice';
import { ConfigProvider, Statistic } from 'antd';
import { debounce } from 'lodash';

import './InfoCardList.scss';

// InfoCard Schema
// {
//   icon: string;
//   id: string;
//   title: string;
//   value: number;
// }

interface IInfoCardListProps {
  currentCategory: string | null;
}

const infoCardColorMap: Record<
  string,
  { icon: ReactNode; iconAreaBgColor: string; colorTextHeading: string }
> = {
  parkingSpaceCount: {
    icon: <DashboardCarIcon />,
    iconAreaBgColor: '#EDF7EB',
    colorTextHeading: '#7FC336',
  },
  stationCount: {
    icon: <DashboardLocationIcon />,
    iconAreaBgColor: '#E5F8F6',
    colorTextHeading: '#20C5B3',
  },
  pileCount: {
    icon: <DashboardPileIcon />,
    iconAreaBgColor: '#F1F8FF',
    colorTextHeading: '#507ACD',
  },
  gunCount: {
    icon: <DashboardGunIcon />,
    iconAreaBgColor: '#F6F1FF',
    colorTextHeading: '#7B61FF',
  },
  members: {
    icon: <DashboardMemberIcon />,
    iconAreaBgColor: '#FFF6E9',
    colorTextHeading: '#F29500',
  },
};

interface IInfoCard {
  id: string;
  title: string;
  value: number;
  isLoading: boolean;
}

function InfoCard({ id, title, value, isLoading }: IInfoCard) {
  if (!infoCardColorMap[id]) {
    return null;
  }

  return (
    <div className="info-card">
      <div
        className="info-card__icon-area"
        style={{ backgroundColor: infoCardColorMap[id].iconAreaBgColor }}
      >
        {infoCardColorMap[id].icon}
      </div>
      <div className="info-card__info-area">
        <ConfigProvider
          theme={{
            components: {
              Statistic: {
                colorTextHeading: infoCardColorMap[id].colorTextHeading,
              },
            },
          }}
        >
          <Statistic
            title={title}
            value={value}
            loading={isLoading}
            className="info-card__statistic"
          />
        </ConfigProvider>
      </div>
    </div>
  );
}

function InfoCardList({ currentCategory }: IInfoCardListProps) {
  const { t } = useTranslation();
  const [infoDetails, setInfoDetails] = useState<
    Record<keyof typeof infoCardColorMap, number>
  >({
    parkingSpaceCount: 0,
    stationCount: 0,
    pileCount: 0,
    gunCount: 0,
    members: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const sellerId = useSelector((state: IRootState) => state.userSlice.sellerId);
  const dispatch = useDispatch<AppDispatch>();

  const exampleInfoCardList: { id: string; title: string }[] = [
    {
      id: 'parkingSpaceCount',
      title: t('InfoCardList.TotalParkingSpaces'),
    },
    {
      id: 'stationCount',
      title: t('InfoCardList.TotalStations'),
    },
    {
      id: 'pileCount',
      title: t('InfoCardList.TotalPiles'),
    },
    {
      id: 'gunCount',
      title: t('InfoCardList.TotalGuns'),
    },
    {
      id: 'members',
      title: t('InfoCardList.TotalMembers'),
    },
  ];

  async function getInfoDetails() {
    setIsLoading(true);
    try {
      const sellerInfo = await api.post(
        '/api/statistics-service/operationView/getStationfacilitiesCountsBySeller',
        {
          sellerId,
          stationId: 0,
          timeSharing: 'TODAY',
        },
      );
      const {
        data: { data, code },
      } = sellerInfo;
      if (code !== 0) {
        console.error('An error occurred while fetching sellerInfo');
        return;
      }

      setInfoDetails((prevInfoDetails) => ({
        ...prevInfoDetails,
        parkingSpaceCount: data.gunCount,
        stationCount: data.stationCount,
        pileCount: data.pileCount,
        gunCount: data.gunCount,
      }));
    } catch (error) {
      console.error('An error occurred while fetching sellerInfo');
    }

    try {
      const memberInfo = await api.post(
        '/api/member-service/member/getMemberNumNew',
        {
          sellerId,
        },
      );
      const {
        data: { data, code },
      } = memberInfo;
      if (code !== 0) {
        console.error('An error occurred while fetching memberInfo');
        return;
      }

      setInfoDetails((prevInfoDetails) => ({
        ...prevInfoDetails,
        members: data.members,
      }));
    } catch (error) {
      console.error('An error occurred while fetching memberInfo');
    }
    setIsLoading(false);
    // setTimeout(() => setIsLoading(false), 1000);
  }

  useEffect(() => {
    getInfoDetails();
  }, []);

  useEffect(() => {
    if (!infoDetails) return;
    dispatch(setBasicInfo(infoDetails));
  }, [infoDetails]);

  useEffect(() => {
    if (!currentCategory) {
      return undefined;
    }
    const debouncedGetInfoDetails = debounce(getInfoDetails, 1500);
    debouncedGetInfoDetails();

    return () => {
      debouncedGetInfoDetails.cancel();
    };
  }, [currentCategory]);

  return (
    <div className="info-card-list">
      {exampleInfoCardList.map((infoCard) => (
        <InfoCard
          key={infoCard.id}
          id={infoCard.id}
          title={infoCard.title}
          value={infoDetails[infoCard.id]}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
}

export default InfoCardList;
